"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";

/* Os dois lados da mesma venda, conectados: a cliente compra no app
   da marca (esquerda) e o push estoura no app do gestor (direita),
   com o pulso viajando pela linha. Loop autônomo. Marca fictícia SUALOJA;
   fotos reais de loja Shoppub (Larulp, cdn.shoppub.io). */

const CDN = "https://cdn.shoppub.io/cdn-cgi/image";
const P = (size: number, path: string) =>
  `${CDN}/w=${size},h=${size},q=80,f=auto/larulp/media/uploads/produtos/foto/${path}`;

const FOTO_BANNER = "/media/lookbook/verao.webp";
const FOTO_PDP = P(500, "lrxzruat/2012061_001_01.jpg");
const FOTO_A = P(300, "vurmeccg/whatsapp-image-2026-08-20-at-084926.jpeg");
const FOTO_B = P(300, "hbyxyfqd/whatsapp-image-2026-08-20-at-084925-2.jpeg");

type Fase = "vitrine" | "pdp" | "comprado" | "viajando" | "push";

const CHIPS = [
  "Notificações push de status e campanhas",
  "Cupom exclusivo no app",
  "Venda notificada em tempo real",
];

export default function AppPush() {
  const raiz = useRef<HTMLDivElement>(null);
  const [on, setOn] = useState(false);
  const [fase, setFase] = useState<Fase>("vitrine");
  const [ciclo, setCiclo] = useState(0);

  useEffect(() => {
    const el = raiz.current;
    if (!el) return;
    if (el.getBoundingClientRect().top < window.innerHeight) {
      setOn(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => e.isIntersecting && (setOn(true), io.disconnect()),
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!on) return;
    let vivo = true;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const t = (fn: () => void, ms: number) => timers.push(setTimeout(() => vivo && fn(), ms));
    const rodada = () => {
      setFase("vitrine");
      t(() => setFase("pdp"), 2200);
      t(() => setFase("comprado"), 4600);
      t(() => setFase("viajando"), 5400);
      t(() => setFase("push"), 6200);
      t(() => {
        setCiclo((c) => c + 1);
        rodada();
      }, 9200);
    };
    rodada();
    return () => {
      vivo = false;
      timers.forEach(clearTimeout);
    };
  }, [on]);

  const vendeu = fase === "push";

  return (
    <section className="overflow-hidden bg-mist">
      <div ref={raiz} className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
        {/* cabeçalho */}
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div>
            <p className="eyebrow text-smoke">(App próprio da sua marca)</p>
            <h2 className="mt-4 max-w-xl text-[clamp(2.1rem,4.6vw,3.8rem)] font-bold leading-[1.02]">
              Sua loja no bolso do cliente. <span className="mark-lime">E do lojista.</span>
            </h2>
            <p className="mt-5 max-w-lg text-lg leading-relaxed text-smoke">
              Aplicativo publicado com a cara da sua marca — e cada venda estourando em
              tempo real no celular de quem comanda.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <span className="text-[12px] font-bold uppercase tracking-wider text-smoke">
              Publicado nas lojas oficiais
            </span>
            <span className="flex gap-2.5">
              <Image src="/media/app/appstore.svg" alt="Disponível na App Store" width={120} height={40} className="h-10 w-auto" />
              <Image src="/media/app/googleplay.svg" alt="Disponível no Google Play" width={135} height={40} className="h-10 w-auto" />
            </span>
          </div>
        </div>

        {/* composição: app da cliente → linha → app do gestor */}
        <div className="relative mt-14 grid items-center gap-10 lg:mt-16 lg:grid-cols-[auto_1fr_auto] lg:gap-8">
          {/* phone da CLIENTE */}
          <div className="justify-self-center">
            <div className="w-[264px] rounded-[42px] bg-ink p-2.5 shadow-[0_50px_100px_-45px_rgba(10,10,11,0.55)]">
              <div className="relative h-[480px] overflow-hidden rounded-[34px] bg-paper">
                {/* header do app da marca */}
                <div className="flex items-center justify-between px-4 pb-2 pt-5">
                  <span className="font-display text-sm font-bold tracking-tight">SUALOJA®</span>
                  <span className="relative">
                    <svg viewBox="0 0 24 24" className="h-4.5 w-4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 7h12l1 13H5L6 7z" />
                      <path d="M9 10V6a3 3 0 016 0v4" />
                    </svg>
                    <span
                      className={`absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-lime text-[9px] font-bold transition-all duration-300 ${
                        fase === "comprado" || fase === "viajando" || fase === "push"
                          ? "scale-100"
                          : "scale-0"
                      }`}
                    >
                      1
                    </span>
                  </span>
                </div>

                {/* vitrine */}
                <div
                  className={`absolute inset-x-0 bottom-0 top-[52px] px-3 transition-all duration-500 ${
                    fase === "vitrine" ? "translate-x-0 opacity-100" : "-translate-x-6 opacity-0"
                  }`}
                >
                  <div className="relative overflow-hidden rounded-2xl">
                    <img src={FOTO_BANNER} alt="" loading="lazy" decoding="async" className="h-36 w-full object-cover object-[50%_20%]" />
                    <span className="absolute bottom-2 left-2.5 rounded-full bg-paper/90 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider">
                      Cupom APP10 · só no app
                    </span>
                  </div>
                  <div className="mt-2.5 grid grid-cols-2 gap-2.5">
                    {[
                      { f: FOTO_A, n: "Top Alana", p: "R$ 189,90" },
                      { f: FOTO_B, n: "Legging Doreen", p: "R$ 299,90" },
                    ].map((x) => (
                      <div key={x.n}>
                        <img src={x.f} alt="" loading="lazy" decoding="async" className="aspect-[3/4] w-full rounded-xl object-cover" />
                        <div className="mt-1 text-[10px] font-semibold">{x.n}</div>
                        <div className="text-[10px] text-smoke">{x.p}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* PDP + compra */}
                <div
                  className={`absolute inset-x-0 bottom-0 top-[52px] px-3 transition-all duration-500 ${
                    fase === "vitrine" ? "translate-x-6 opacity-0" : "translate-x-0 opacity-100"
                  }`}
                >
                  <img src={FOTO_PDP} alt="" loading="lazy" decoding="async" className="h-56 w-full rounded-2xl object-cover object-top" />
                  <div className="mt-2 flex items-baseline justify-between">
                    <span className="text-[12px] font-bold">Camisa Winnie Movetech</span>
                    <span className="text-[12px] font-bold">R$ 699,90</span>
                  </div>
                  <div className="mt-1.5 flex gap-1.5">
                    {["P", "M", "G", "GG"].map((tam, i) => (
                      <span
                        key={tam}
                        className={`flex h-7 w-8 items-center justify-center rounded-lg border text-[10px] font-bold ${
                          i === 2 ? "border-ink bg-ink text-lime" : "border-line text-ink/60"
                        }`}
                      >
                        {tam}
                      </span>
                    ))}
                  </div>
                  <div
                    className={`mt-3 flex h-11 items-center justify-center rounded-full text-[12px] font-bold transition-all duration-300 ${
                      fase === "pdp"
                        ? "scale-100 bg-lime text-ink"
                        : "scale-[0.97] bg-ink text-lime"
                    }`}
                  >
                    {fase === "pdp" ? "Comprar agora" : "✓ Pedido feito — obrigado, Marina!"}
                  </div>
                </div>
              </div>
            </div>
            <p className="mt-3 text-center text-[12px] font-semibold text-smoke">
              O app da sua marca, na mão da cliente
            </p>
          </div>

          {/* linha de conexão + chips */}
          <div className="relative hidden self-center lg:block">
            <div className="flex flex-col gap-2.5 pb-8">
              {CHIPS.map((c) => (
                <span
                  key={c}
                  className="mx-auto rounded-full border border-line bg-paper px-4 py-2 text-[12px] font-semibold text-ink/75"
                >
                  {c}
                </span>
              ))}
            </div>
            <div className="relative h-[2px] w-full overflow-visible rounded bg-ink/15">
              {fase === "viajando" && (
                <span
                  key={ciclo}
                  className="absolute -top-[5px] h-3 w-3 rounded-full bg-lime shadow-[0_0_18px_4px_rgba(201,241,88,0.65)]"
                  style={{ animation: "viaja 0.8s linear forwards" }}
                />
              )}
              <span className={`absolute -top-[3px] left-0 h-2 w-2 rounded-full ${fase === "viajando" || vendeu ? "bg-lime" : "bg-ink/30"}`} />
              <span className={`absolute -top-[3px] right-0 h-2 w-2 rounded-full ${vendeu ? "bg-lime" : "bg-ink/30"}`} />
            </div>
            <p className="mt-3 text-center text-[11px] font-bold uppercase tracking-[0.18em] text-smoke">
              {vendeu ? "venda sincronizada ✓" : "uma venda a caminho…"}
            </p>
            <style>{`@keyframes viaja { from { left: 0% } to { left: calc(100% - 12px) } }`}</style>
          </div>

          {/* phone do GESTOR */}
          <div className="justify-self-center">
            <div className="w-[264px] rounded-[42px] bg-ink p-2.5 shadow-[0_50px_100px_-45px_rgba(10,10,11,0.55)]">
              <div className="relative h-[480px] overflow-hidden rounded-[34px] bg-[#f1f1f3] text-ink">
                <div className="pointer-events-none select-none px-3 pt-3">
                  {/* header do app do gestor */}
                  <div className="flex items-center justify-between">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-paper shadow-[0_2px_8px_rgba(10,10,11,0.08)]">
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 9a3 3 0 10-6 0 3 3 0 006 0zM6 20c.6-3.2 3-5 6-5s5.4 1.8 6 5" />
                      </svg>
                    </span>
                    <Image src="/brand/logo_shoppub_preto.svg" alt="Shoppub" width={78} height={22} className="h-[18px] w-auto" />
                    <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-paper shadow-[0_2px_8px_rgba(10,10,11,0.08)]">
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 16v-5a6 6 0 10-12 0v5l-1.5 2h15zM10 20a2 2 0 004 0" />
                      </svg>
                      <span
                        className={`absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-lime transition-transform duration-300 ${
                          vendeu ? "scale-100" : "scale-0"
                        }`}
                      />
                    </span>
                  </div>

                  {/* seletor de loja */}
                  <div className="mt-2 flex items-center gap-2 rounded-xl bg-paper px-2.5 py-1.5 shadow-[0_2px_8px_rgba(10,10,11,0.05)]">
                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-ink text-[8px] font-bold text-paper">SU</span>
                    <span className="text-[11px] font-semibold">sualoja.com.br</span>
                    <span className="ml-auto text-[10px] text-smoke">trocar</span>
                  </div>

                  {/* períodos */}
                  <div className="mt-2 flex gap-1.5">
                    {["Hoje", "Ontem", "7 dias", "15 dias", "30 dias"].map((p, i) => (
                      <span
                        key={p}
                        className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[9.5px] font-semibold ${
                          i === 0 ? "bg-ink text-paper" : "bg-paper text-ink/70"
                        }`}
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                  <div className="mt-1 text-right text-[9px] font-semibold text-ink/60">Todos os canais ⇅</div>

                  {/* pedidos pagos */}
                  <div className="mt-1 rounded-2xl bg-paper p-2.5 shadow-[0_2px_8px_rgba(10,10,11,0.05)]">
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="flex items-center gap-1.5 font-semibold text-ink/80">
                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18" /></svg>
                        Pedidos pagos
                      </span>
                      <span className="flex items-center gap-1 text-ink/60">◎ Definir meta</span>
                    </div>
                    <div className="mt-1 font-display font-bold leading-none">
                      <span className="text-[13px] text-ink/60">R$ </span>
                      <span className="text-[23px] tracking-tight">{vendeu ? "13.180,00" : "12.480,10"}</span>
                    </div>
                    <div className={`mt-1 text-[9px] font-semibold ${vendeu ? "text-lime-deep" : "text-smoke"}`}>
                      {vendeu ? "+1 pedido agora mesmo" : "aguardando novos pedidos…"}
                    </div>
                  </div>

                  {/* indicadores */}
                  <div className="mt-2 flex items-center justify-between px-0.5">
                    <span className="font-display text-[12px] font-bold">Indicadores</span>
                    <span className="text-[9px] font-semibold text-ink/60">⚙ Editar</span>
                  </div>
                  <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                    {[
                      { icone: "M8 6h13l-2 8H9L7 4H4M10 20a1 1 0 100-2 1 1 0 000 2zM17 20a1 1 0 100-2 1 1 0 000 2z", label: "Pedidos realizados", valor: vendeu ? "19" : "18" },
                      { icone: "M4 9a2 2 0 002-2h12a2 2 0 002 2v2a2 2 0 000 4v2a2 2 0 00-2 2H6a2 2 0 00-2-2v-2a2 2 0 000-4zM13 7v10", label: "Ticket médio", valor: "R$ 693,34" },
                      { icone: "M12 5C6 5 2 12 2 12s4 7 10 7 10-7 10-7-4-7-10-7zM12 15a3 3 0 110-6 3 3 0 010 6z", label: "Visitantes", valor: "2.412" },
                      { icone: "M6 18L18 6M8 9a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM16 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z", label: "Conversão", valor: "3,2%" },
                    ].map((k) => (
                      <div key={k.label} className="rounded-xl bg-paper p-2 shadow-[0_2px_8px_rgba(10,10,11,0.05)]">
                        <div className="flex items-center gap-1.5 text-[9px] font-medium text-ink/70">
                          <svg viewBox="0 0 24 24" className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={k.icone} /></svg>
                          <span className="truncate">{k.label}</span>
                        </div>
                        <div className="mt-0.5 font-display text-[15px] font-bold">{k.valor}</div>
                      </div>
                    ))}
                  </div>

                  {/* últimos pedidos */}
                  <div className="mt-2.5 flex items-center justify-between px-0.5">
                    <span className="font-display text-[12px] font-bold">Últimos pedidos</span>
                    <span className="text-[9px] font-semibold text-ink/60">Ver todos ›</span>
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 rounded-xl bg-paper px-2.5 py-1.5 shadow-[0_2px_8px_rgba(10,10,11,0.05)]">
                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[#f1f1f3] text-[8px] font-bold text-ink/60">MA</span>
                    <span className="min-w-0 text-[10px] font-semibold">
                      {vendeu ? "Marina A." : "Estela R."}
                      <span className="block text-[8.5px] font-normal text-smoke">{vendeu ? "agora · São Paulo" : "há 12 min · Campinas"}</span>
                    </span>
                    <span className="ml-auto text-right text-[10px] font-bold">
                      {vendeu ? "R$ 699,90" : "R$ 429,80"}
                      <span className="block rounded-full bg-lime/25 px-1.5 text-[8px] font-bold text-[#3f6b0e]">Pago</span>
                    </span>
                  </div>
                </div>

                {/* nav inferior do app */}
                <div className="absolute inset-x-6 bottom-3 flex items-center justify-between rounded-full bg-paper px-5 py-2 shadow-[0_10px_28px_-10px_rgba(10,10,11,0.28)]">
                  {[
                    ["Início", "M4 20h16M6 16v-4M11 16V8M16 16v-6", true],
                    ["Pedidos", "M6 7h12l1 13H5L6 7zM9 10V6a3 3 0 016 0v4", false],
                    ["Histórico", "M12 8v4l3 2M21 12a9 9 0 11-9-9 9 9 0 019 9z", false],
                  ].map(([nome, d, ativo]) => (
                    <span key={nome as string} className={`flex flex-col items-center gap-0.5 ${ativo ? "text-ink" : "text-ink/60"}`}>
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={ativo ? 2.2 : 1.8} strokeLinecap="round" strokeLinejoin="round"><path d={d as string} /></svg>
                      <span className="text-[8px] font-bold">{nome}</span>
                    </span>
                  ))}
                </div>

                {/* push sincronizada */}
                <div
                  className={`absolute inset-x-2.5 top-2.5 flex items-center gap-2.5 rounded-2xl border border-line bg-paper/95 p-2.5 shadow-[0_14px_30px_-14px_rgba(10,10,11,0.4)] backdrop-blur transition-all duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] ${
                    vendeu ? "translate-y-0 opacity-100" : "-translate-y-14 opacity-0"
                  }`}
                >
                  <span className="h-8 w-8 shrink-0 overflow-hidden rounded-lg">
                    <Image src="/brand/favicon_preto.svg" alt="" width={32} height={32} className="h-full w-full" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[11px] font-bold">Nova venda realizada!</span>
                    <span className="block text-[10px] text-smoke">R$ 699,90 · Camisa Winnie · agora</span>
                  </span>
                </div>
              </div>
            </div>
            <p className="mt-3 text-center text-[12px] font-semibold text-smoke">
              O painel do gestor, no seu bolso
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
